import type { MatchingLink, NoFeedDestination, UnresolvedLink } from './types.js';

/**
 * Which destination an unclaimed feed's "Claim for" picker opens on, and why.
 * Three kinds of target: an open registration (a link the buy side pushed
 * that could not be resolved to an account - Match assigns it), a destination
 * with no OPEN link - active or stopped: Airman reports it without a feed, or
 * its links were all unlinked or ended - for which Match creates the link,
 * and a destination whose link is only suggested, to another feed, which
 * Match moves to this feed. A guess only ever PRESELECTS; the person confirms
 * by pressing Match (owner rule 2026-09-12: a link is confirmed by a person,
 * never guessed).
 *
 * The closest match (owner ask 2026-09-18: "review the list of feeds from
 * Airman and suggest the closest matching one") is the entry that points at
 * this feed number: the registration that pushed it; then, among the
 * registrations and the destinations, the one whose CHANNEL is the number,
 * whose URL carries it (`y3581` in the path, `ref=30247715` in the query -
 * matching/url-ids.ts), or whose name carries it as a whole number ("David -
 * CSE - Chrome - WW - 5k PN1016") - a live destination before a stopped one,
 * the feed's own rep before another. Nothing pointing at the feed: the first
 * registration, then the first live destination, then the rest, with no
 * "closest" note.
 */
export type OpenRegistration = Pick<
  UnresolvedLink,
  | 'id'
  | 'destinationId'
  | 'destinationName'
  | 'feedId'
  | 'ownerRep'
  | 'updatedAt'
  | 'channel'
  | 'urlIds'
  | 'ownerKnown'
>;
export type FreeDestination = Pick<
  NoFeedDestination,
  | 'destinationId'
  | 'destinationName'
  | 'ownerRep'
  | 'reason'
  | 'status'
  | 'linksEnded'
  | 'channel'
  | 'urlIds'
>;
/** A destination whose link is only suggested, to another feed: Match moves that suggestion to this feed (2026-09-18). */
export type SuggestedElsewhere = Pick<
  MatchingLink,
  | 'id'
  | 'destinationId'
  | 'destinationName'
  | 'feedId'
  | 'ownerRep'
  | 'updatedAt'
  | 'channel'
  | 'urlIds'
>;

export type ClaimTarget =
  | { kind: 'registration'; reg: OpenRegistration }
  | { kind: 'destination'; dest: FreeDestination }
  | { kind: 'suggested'; link: SuggestedElsewhere };

/** The preselected entry and the one-line reason the picker shows beside it. */
export interface ClaimHint {
  key: string;
  why: string;
}

const LINK = 'link:';
const DEST = 'dest:';
const SUGG = 'sugg:';

export const registrationKey = (id: number): string => `${LINK}${id}`;
export const destinationKey = (id: string): string => `${DEST}${id}`;
export const suggestedKey = (id: number): string => `${SUGG}${id}`;

/** True when `name` carries `feedId` as a whole number: "PN1016" and
 * "YHS 24939 US" do for 1016 and 24939; "YHS-21001" does not for 2100. */
export function mentionsFeed(name: string | null | undefined, feedId: string): boolean {
  if (!name || !feedId) return false;
  const esc = feedId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`(^|[^0-9])${esc}([^0-9]|$)`, 'i').test(name);
}

const digits = (s: string): string => s.replace(/\D+/g, '');

/** The same number under a provider's letter prefix: PN1016 is 1016, y3581 is 3581 (three digits at least, so 'v2' never is). */
function sameNumber(a: string | null | undefined, b: string): boolean {
  if (!a || !b) return false;
  if (a.trim().toLowerCase() === b.trim().toLowerCase()) return true;
  const da = digits(a);
  return da.length >= 3 && da === digits(b);
}

type Pointer = {
  channel?: string | null;
  urlIds?: readonly { name: string; value: string }[];
  destinationName: string | null;
};

/** How an entry points at the feed number, or null when it does not. */
function pointsAt(e: Pointer, feedId: string): string | null {
  if (sameNumber(e.channel, feedId)) return `its channel is ${e.channel}`;
  const url = (e.urlIds ?? []).find((i) => sameNumber(i.value, feedId));
  if (url) return `its URL carries ${url.value}`;
  if (mentionsFeed(e.destinationName, feedId)) return `its name carries ${feedId}`;
  return null;
}

export function closestClaimTarget(
  feed: { feedId: string; rep: string },
  open: readonly OpenRegistration[],
  free: readonly FreeDestination[],
  suggested: readonly SuggestedElsewhere[] = [],
  knownReps: ReadonlySet<string> = new Set(),
): ClaimHint | null {
  const sameRep = (rep: string | null) =>
    !!rep && rep.trim().toLowerCase() === feed.rep.trim().toLowerCase();
  // Another rep's entry is never the closest match: one provider issues a number
  // to several people, and the owner decides (review 2026-09-18). It stays in
  // the list for a deliberate pick.
  const otherRep = (rep: string | null, known: boolean) => known && !!rep && !sameRep(rep);
  const ownEntries = {
    open: open.filter((o) => !otherRep(o.ownerRep, o.ownerKnown)),
    free: free.filter(
      (d) => !otherRep(d.ownerRep, !!d.ownerRep && knownReps.has(d.ownerRep.toLowerCase())),
    ),
    suggested: suggested.filter(
      (l) => !otherRep(l.ownerRep, !!l.ownerRep && knownReps.has(l.ownerRep.toLowerCase())),
    ),
  };
  open = ownEntries.open;
  free = ownEntries.free;
  suggested = ownEntries.suggested;
  const pushedAll = open.filter((o) => sameNumber(o.feedId, feed.feedId));
  const pushed = pushedAll.find((o) => sameRep(o.ownerRep)) ?? pushedAll[0];
  if (pushed) return { key: registrationKey(pushed.id), why: `it pushed feed ${pushed.feedId}` };
  const pick = <T extends { ownerRep: string | null }>(
    items: readonly T[],
    live: (t: T) => boolean,
  ): T | undefined =>
    items.find((t) => live(t) && sameRep(t.ownerRep)) ??
    items.find(live) ??
    items.find((t) => sameRep(t.ownerRep)) ??
    items[0];
  const openHits = open
    .map((o) => ({ o, why: pointsAt(o, feed.feedId) }))
    .filter((h): h is { o: OpenRegistration; why: string } => h.why != null);
  const openPick = pick(
    openHits.map((h) => ({ ...h, ownerRep: h.o.ownerRep })),
    () => true,
  );
  if (openPick) return { key: registrationKey(openPick.o.id), why: openPick.why };
  const freeHits = free
    .map((d) => ({ d, why: pointsAt(d, feed.feedId) }))
    .filter((h): h is { d: FreeDestination; why: string } => h.why != null);
  const freePick = pick(
    freeHits.map((h) => ({ ...h, ownerRep: h.d.ownerRep })),
    (h) => h.d.status === 'active',
  );
  if (freePick)
    return {
      key: destinationKey(freePick.d.destinationId),
      why: `${freePick.why}${freePick.d.status !== 'active' ? ' (stopped in Airman)' : ''}`,
    };
  const suggHits = suggested
    .map((l) => ({ l, why: pointsAt(l, feed.feedId) }))
    .filter((h): h is { l: SuggestedElsewhere; why: string } => h.why != null);
  const suggPick = pick(
    suggHits.map((h) => ({ ...h, ownerRep: h.l.ownerRep })),
    () => true,
  );
  if (suggPick)
    return {
      key: suggestedKey(suggPick.l.id),
      why: `${suggPick.why} (suggested to feed ${suggPick.l.feedId})`,
    };
  return null;
}

export function preferredClaimTarget(
  feed: { feedId: string; rep: string },
  open: readonly OpenRegistration[],
  free: readonly FreeDestination[],
  suggested: readonly SuggestedElsewhere[] = [],
  knownReps: ReadonlySet<string> = new Set(),
): string | null {
  const closest = closestClaimTarget(feed, open, free, suggested, knownReps);
  if (closest) return closest.key;
  if (open[0]) return registrationKey(open[0].id);
  const active = free.find((d) => d.status === 'active');
  if (active) return destinationKey(active.destinationId);
  if (suggested[0]) return suggestedKey(suggested[0].id);
  if (free[0]) return destinationKey(free[0].destinationId);
  return null;
}

/** The target a picker value names, or null when it names nothing (a stale pick). */
export function resolveClaimTarget(
  key: string | null,
  open: readonly OpenRegistration[],
  free: readonly FreeDestination[],
  suggested: readonly SuggestedElsewhere[] = [],
): ClaimTarget | null {
  if (!key) return null;
  if (key.startsWith(LINK)) {
    const id = Number(key.slice(LINK.length));
    const reg = open.find((o) => o.id === id);
    return reg ? { kind: 'registration', reg } : null;
  }
  if (key.startsWith(DEST)) {
    const id = key.slice(DEST.length);
    const dest = free.find((d) => d.destinationId === id);
    return dest ? { kind: 'destination', dest } : null;
  }
  if (key.startsWith(SUGG)) {
    const id = Number(key.slice(SUGG.length));
    const link = suggested.find((l) => l.id === id);
    return link ? { kind: 'suggested', link } : null;
  }
  return null;
}
